import styled, { css } from "styled-components";
import { Link } from "react-router-dom";
import colors from "@styling/colors";
import { borderRadius, spacing } from "@styling/sizing";
import { fontSizes } from "@styling/fonts";

export type ButtonColor =
  | "primary"
  | "secondary"
  | "danger"
  | "success"
  | "warning"
  | "info";

type ButtonExtraProps = {
  $variant?: "filled" | "outlined";
  $small?: boolean;
  $color?: ButtonColor;
};

type ButtonTone = {
  fill: string;
  fillText: string;
  outline: string;
  outlineText: string;
  outlineHoverBg: string;
  outlineHoverText: string;
};

const tones: Record<ButtonColor, ButtonTone> = {
  primary: {
    fill: colors.primary,
    fillText: colors.primaryContrastText,
    outline: colors.primary,
    outlineText: colors.primary,
    outlineHoverBg: colors.primary,
    outlineHoverText: colors.primaryContrastText,
  },
  secondary: {
    fill: colors.backgroundDark,
    fillText: colors.text,
    outline: colors.border,
    outlineText: colors.text,
    outlineHoverBg: colors.backgroundDark,
    outlineHoverText: colors.text,
  },
  danger: {
    fill: colors.danger,
    fillText: colors.primaryContrastText,
    outline: colors.danger,
    outlineText: colors.danger,
    outlineHoverBg: colors.danger,
    outlineHoverText: colors.primaryContrastText,
  },
  success: {
    fill: colors.success,
    fillText: colors.primaryContrastText,
    outline: colors.success,
    outlineText: colors.success,
    outlineHoverBg: colors.success,
    outlineHoverText: colors.primaryContrastText,
  },
  warning: {
    fill: colors.warning,
    fillText: colors.warningText,
    outline: colors.warning,
    outlineText: colors.warningText,
    outlineHoverBg: colors.warning,
    outlineHoverText: colors.warningText,
  },
  info: {
    fill: colors.info,
    fillText: colors.primaryContrastText,
    outline: colors.info,
    outlineText: colors.info,
    outlineHoverBg: colors.info,
    outlineHoverText: colors.primaryContrastText,
  },
};

const filledStyles = (color: ButtonColor) => {
  const tone = tones[color];
  return css`
    border: 1px solid transparent;
    background-color: ${tone.fill};
    color: ${tone.fillText};
    transition: opacity 0.2s;

    &:hover:not(:disabled) {
      opacity: 0.9;
    }
  `;
};

const outlinedStyles = (color: ButtonColor) => {
  const tone = tones[color];
  return css`
    background-color: transparent;
    border-style: solid;
    border-width: 1px;
    border-color: ${tone.outline};
    color: ${tone.outlineText};
    transition:
      background-color 0.15s,
      color 0.15s,
      border-color 0.15s,
      opacity 0.2s;

    &:hover:not(:disabled) {
      background-color: ${tone.outlineHoverBg};
      color: ${tone.outlineHoverText};
    }
  `;
};

export const Button = styled.button<ButtonExtraProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${spacing.x1}px;
  border-radius: ${borderRadius.regular}px;
  padding: ${({ $small }) =>
    $small
      ? `${spacing.x1}px ${spacing.x2}px`
      : `${spacing.x2}px ${spacing.x3}px`};
  font-size: ${({ $small }) => ($small ? fontSizes.small : fontSizes.regular)}px;
  font-weight: 600;
  cursor: pointer;
  line-height: 1em;

  ${({ $variant = "filled", $color = "primary" }) =>
    $variant === "outlined" ? outlinedStyles($color) : filledStyles($color)}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const ButtonLink = styled(Button).attrs({ as: "a" })`
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

export const RouterButtonLink = styled(Button).attrs({ as: Link })`
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
`;

export const IconButton = styled.button<{ $color?: ButtonColor }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  background: none;
  color: ${colors.textMuted};
  cursor: pointer;
  border-radius: ${borderRadius.regular}px;

  &:hover:not(:disabled) {
    color: ${({ $color = "primary" }) =>
      $color === "primary" || $color === "secondary"
        ? colors.text
        : tones[$color].fill};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
